// external libraries
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// constants
import { ROSTER_PLAN_FILTER_DESCRIPTORS } from '../RosterPlan.consts';

// types
import type { TFunction } from 'i18next';
import type { EmployeeDutyData } from 'RestAPIs/cost-centers/employees/types';
import type { OpenRequestResponse } from 'RestAPIs/roster/open-requests/types';
import type { QualificationResponse } from 'RestAPIs/roster/qualifications/types';
import type {
  FilterOptionType,
  RosterFilterSource,
  RosterPlanFilter,
  RosterPlanFilterDescriptor
} from '../RosterPlan.types';

// utils
import { getUniqueFilterValues } from '../utils/rosterPlanFilterOptions';
import { collectAllDuties } from '../utils/collectAllDuties';
import { getAllDutyHourTypes } from '../utils/getAllDutyHourTypes';

export type UseRosterPlanFilterOptionsProps = {
  employees?: EmployeeDutyData[];
  openRequests?: OpenRequestResponse[];
  qualificationsByCostCenters?: QualificationResponse[];
};

type FilterSources = Record<RosterFilterSource, unknown[]>;

/**
 * The descriptor's own fields, read through the widened type. The list is `as const`, so an entry
 * that sets no `label` has no such property at all — reading it off the literal union would not
 * compile. Taking `key` straight from the literal keeps it narrowed to `FilterKey`.
 */
const toFilterFields = (
  descriptor: RosterPlanFilterDescriptor,
  sources: FilterSources,
  t: TFunction
) => ({
  label: descriptor.label,
  section: descriptor.section,
  options: buildOptions(descriptor, sources, t)
});

const buildOptions = (
  descriptor: RosterPlanFilterDescriptor,
  sources: FilterSources,
  t: TFunction
): FilterOptionType[] => {
  if ('source' in descriptor) {
    return getUniqueFilterValues({
      data: sources[descriptor.source],
      labelKey: descriptor.labelKey,
      idKey: descriptor.idKey
    });
  }

  return descriptor.options.map(({ id, labelKey }) => ({
    id,
    label: t(`filters.${labelKey}`)
  }));
};

/**
 * The options behind the roster filter popper: one entry per group in
 * `ROSTER_PLAN_FILTER_DESCRIPTORS`, in that same order.
 *
 * The data driven groups are all the same operation — the distinct label/id pairs of one field pair
 * in one collection — so the groups live as descriptors and this hook only supplies the collections
 * and the translations.
 */
const useRosterPlanFilterOptions = ({
  employees,
  openRequests,
  qualificationsByCostCenters
}: UseRosterPlanFilterOptionsProps): RosterPlanFilter[] => {
  const { t } = useTranslation('common', { keyPrefix: 'rosterPlan' });

  /**
   * Explicit `useMemo` and no React Compiler directive, against the wider repo's habit. This
   * hook's own return identity is not load bearing; it follows the same rule as the other five so
   * the folder has one convention rather than a per-hook judgement call. Reasoning in README.md,
   * "Decisions".
   */
  return useMemo(() => {
    const duties = collectAllDuties(employees);

    const sources: FilterSources = {
      employees: employees ?? [],
      openRequests: openRequests ?? [],
      qualifications: qualificationsByCostCenters ?? [],
      duties,
      dutyHourTypes: getAllDutyHourTypes(duties),
      openRequestDutyHourTypes: getAllDutyHourTypes(openRequests)
    };

    return ROSTER_PLAN_FILTER_DESCRIPTORS.map((descriptor) => ({
      key: descriptor.key,
      ...toFilterFields(descriptor, sources, t)
    }));
  }, [employees, openRequests, qualificationsByCostCenters, t]);
};

export default useRosterPlanFilterOptions;
